import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { getCurrentMonthSpending } from '../utils/guiltFree';
import { formatINR } from '../utils/leakBuckets';
import { getCategoryColor } from '../utils/categorization';
import { isGuessTxn, isUncategorizedTxn, REVIEW_FILTER } from '../utils/confidence';
import Filters from './Filters';
import TransactionList from './TransactionList';

/**
 * Spend — where money went by category; re-tag merchants (saved on this phone).
 */
export default function ActivityView() {
    const {
        monthFilteredTransactions,
        allTransactions,
        spendFocusCategory,
        setSpendFocusCategory,
    } = useAppContext();
    const [categoryFilter, setCategoryFilter] = useState('all');

    useEffect(() => {
        if (spendFocusCategory) {
            setCategoryFilter(spendFocusCategory);
            setSpendFocusCategory(null);
        }
    }, [spendFocusCategory, setSpendFocusCategory]);

    const spending = useMemo(
        () => getCurrentMonthSpending(monthFilteredTransactions.length ? monthFilteredTransactions : allTransactions),
        [monthFilteredTransactions, allTransactions]
    );

    const categories = useMemo(() => {
        return Object.entries(spending.byCategory || {})
            .sort((a, b) => b[1] - a[1]);
    }, [spending]);

    const pool = monthFilteredTransactions.length ? monthFilteredTransactions : allTransactions;

    const uncategorizedCount = useMemo(() => {
        return pool
            .filter(t => t.type && String(t.type).toUpperCase() === 'DEBIT')
            .filter(isUncategorizedTxn)
            .length;
    }, [pool]);

    const guessCount = useMemo(() => {
        return pool
            .filter(t => t.type && String(t.type).toUpperCase() === 'DEBIT')
            .filter(isGuessTxn)
            .length;
    }, [pool]);

    const reviewCount = uncategorizedCount + guessCount;

    return (
        <div className="activity-view">
            <p className="leak-eyebrow">Spend</p>
            <h1 className="leak-display page-title">Where it went</h1>
            <p className="leak-sub">Tap a tag to fix — remembered for next time.</p>

            {reviewCount > 0 && (
                <div className="review-chip-row">
                    <button
                        type="button"
                        className={`cat-chip warn ${categoryFilter === REVIEW_FILTER ? 'active' : ''}`}
                        onClick={() => setCategoryFilter(categoryFilter === REVIEW_FILTER ? 'all' : REVIEW_FILTER)}
                    >
                        {reviewCount} to check
                        {guessCount > 0 && uncategorizedCount > 0
                            ? ` · ${guessCount} guess · ${uncategorizedCount} open`
                            : guessCount > 0
                                ? ' · guesses'
                                : ''}
                    </button>
                </div>
            )}

            {categories.length > 0 && (
                <section className="activity-cats">
                    <div className="cat-chip-row">
                        <button
                            type="button"
                            className={`cat-chip ${categoryFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setCategoryFilter('all')}
                        >
                            All · {formatINR(spending.totalSpent)}
                        </button>
                        {categories.map(([cat, amt]) => {
                            const pct = spending.totalSpent > 0 ? Math.round((amt / spending.totalSpent) * 100) : 0;
                            return (
                                <button
                                    key={cat}
                                    type="button"
                                    className={`cat-chip ${categoryFilter === cat ? 'active' : ''}`}
                                    style={{ '--cat-accent': getCategoryColor(cat) }}
                                    onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
                                >
                                    {cat} · {pct}%
                                </button>
                            );
                        })}
                    </div>

                    <ul className="cat-bars">
                        {categories.slice(0, 6).map(([cat, amt]) => {
                            const pct = spending.totalSpent > 0 ? Math.round((amt / spending.totalSpent) * 100) : 0;
                            return (
                                <li key={cat}>
                                    <div className="cat-bar-top">
                                        <span>{cat}</span>
                                        <strong>{formatINR(amt)}</strong>
                                    </div>
                                    <div className="home-gauge-track">
                                        <div
                                            className="home-gauge-fill"
                                            style={{
                                                width: `${pct}%`,
                                                background: getCategoryColor(cat),
                                            }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <Filters />
            <TransactionList categoryFilter={categoryFilter} />
        </div>
    );
}
